import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Activity, BarChart3, Bell, BookOpen, ChevronDown, Database, FileText, LayoutGrid, LogOut, Moon, Radar, ScrollText, Server, Settings as SettingsIcon, Siren, Sun, TrendingUp, Waypoints } from "lucide-react";
import { useAuth, useTheme } from "@/context/AppContext";
import { fetcher } from "@/lib/api";
import { useAlertChime, useSoundPref } from "@/lib/traffic";
import { AlertBanner, SoundToggle } from "@/components/traffic/Widgets";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/overview", label: "Overview", icon: LayoutGrid, id: "overview" },
  { to: "/live", label: "Live", icon: Activity, id: "live" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/incidents", label: "Incidents", icon: Siren, id: "incidents" },
  { to: "/forecast", label: "Forecast", icon: TrendingUp, id: "forecast" },
  { to: "/radar-health", label: "Radars", icon: Radar, id: "radar-health" },
  { to: "/reports", label: "Reports", icon: FileText, id: "reports" },
  { to: "/alerts", label: "Alerts", icon: Bell, id: "alerts" },
];
const SYSTEM = [
  { to: "/settings", label: "Settings", icon: SettingsIcon, id: "settings" },
  { to: "/connections", label: "Connections", icon: Server, id: "connections" },
  { to: "/explorer", label: "Data explorer", icon: Database, id: "explorer" },
  { to: "/mapping", label: "Field mapping", icon: Waypoints, id: "mapping" },
  { to: "/audit", label: "Audit log", icon: ScrollText, id: "audit" },
  { to: "/setup", label: "Setup guide", icon: BookOpen, id: "setup" },
];

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button onClick={toggle} data-testid="theme-toggle-button" aria-label="Toggle dark mode"
      className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

function SystemMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();
  const active = SYSTEM.some((s) => loc.pathname.startsWith(s.to));
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="nav-system" aria-haspopup="menu" aria-expanded={open}
        className={cn("flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors", active ? "bg-secondary font-medium text-foreground" : "text-muted-foreground hover:text-foreground")}>
        <SettingsIcon className="h-4 w-4" /><span className="hidden md:inline">System</span><ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div role="menu" data-testid="system-menu" className="surface absolute right-0 z-40 mt-2 w-52 p-1.5 shadow-xl">
          {SYSTEM.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} data-testid={`nav-${id}`} className={({ isActive }) => cn("flex items-center gap-2 rounded-xl px-3 py-2 text-sm", isActive ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground")}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function PoweredBy() {
  return (
    <footer className="flex items-center justify-center gap-2 border-t border-border/70 py-6 text-xs text-muted-foreground" data-testid="powered-by-footer">
      <span>Powered by</span>
      <img src="/cuvano-logo.png" alt="Cuvano" className="h-2.5 w-auto opacity-70 dark:invert" />
      <span className="font-medium">VIDS</span>
    </footer>
  );
}

function GlobalAlerts() {
  const { data, mutate } = useSWR("/traffic/alerts?open=1&limit=20", fetcher, { refreshInterval: 10000 });
  const { data: settings } = useSWR("/traffic/settings", fetcher);
  const { enabled, toggle } = useSoundPref();
  useAlertChime(data?.alerts, enabled && settings?.sound_enabled !== false, settings?.sound_volume ?? 0.6);
  const loc = useLocation();
  const openCount = (data?.alerts || []).filter((a) => !a.acked_at).length;
  return {
    banner: loc.pathname === "/alerts" ? null : <AlertBanner alerts={data?.alerts} onAck={mutate} compact wall={loc.pathname === "/overview" || loc.pathname === "/live"} />,
    sound: <SoundToggle enabled={enabled} onToggle={toggle} />,
    count: openCount,
  };
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const g = GlobalAlerts();
  const isAdmin = user?.role === "admin";
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-4 px-4 sm:px-8">
          <NavLink to="/overview" className="flex shrink-0 items-center gap-3" data-testid="app-logo">
            <span className="rounded-lg dark:bg-white dark:px-1.5 dark:py-0.5"><img src="/lpt2-logo.png" alt="LPT2" className="h-7 w-auto" /></span>
            <span className="hidden border-l border-border pl-3 text-sm font-semibold tracking-tight 2xl:inline">LPT2 Traffic Operations</span>
          </NavLink>
          <nav className="relative flex flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)] lg:[mask-image:none]" data-testid="main-nav">
            {NAV.map(({ to, label, icon: Icon, id }) => (
              <NavLink key={to} to={to} data-testid={`nav-${id}`}
                className={({ isActive }) => cn("relative flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors",
                  isActive ? "bg-secondary font-medium text-foreground" : "text-muted-foreground hover:text-foreground")}>
                <Icon className="h-4 w-4" />
                <span className="hidden lg:inline">{label}</span>
                {id === "alerts" && g.count > 0 && <span data-testid="nav-alert-count" className="num absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">{g.count}</span>}
              </NavLink>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-1">
            {isAdmin && <SystemMenu />}
            {g.sound}
            <ThemeToggle />
            <span className="hidden px-2 text-xs text-muted-foreground lg:inline" data-testid="current-user-email" title={user?.role}>{user?.email}<span className="ml-1.5 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide" data-testid="current-user-role">{user?.role}</span></span>
            <button onClick={logout} data-testid="logout-button" aria-label="Sign out"
              className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto min-h-[calc(100vh-8rem)] max-w-[1600px] px-4 py-6 sm:px-8 sm:py-8 fade-in">
        {g.banner && <div className="mb-6">{g.banner}</div>}
        <Outlet />
      </main>
      <PoweredBy />
    </div>
  );
}
